import React from 'react'
import { Badge } from '../../../atoms'
import { IconButton } from '../../../atoms/IconButton'
import { Icon } from '../../../atoms/Icon'
import { InfoTooltip } from '../../../InfoTooltip'
import { TooltipMetricExplanation } from '../../../TooltipMetricExplanation'
import {
  Grid,
  Card,
  CardHeader,
  CardTitle,
  MetricGrid,
  MetricRow,
  MetricLabel,
  MetricValue,
  ChartContainer,
  ProgressBar,
  ProgressFill,
} from '../GeoAnalysisSection.styles'
import { calculateTerrainMetrics } from '../utils'
import type { ShapeTabProps } from '../GeoAnalysisSection.types'

// Compass azimuth (clockwise from North) for each facing sector.
const ASPECT_AZIMUTH: Record<string, number> = {
  North: 0,
  'North-East': 45,
  East: 90,
  'South-East': 135,
  South: 180,
  'South-West': 225,
  West: 270,
  'North-West': 315,
}

/**
 * Shape tab component showing plot boundary visualization and shape metrics
 */
export const ShapeTab: React.FC<ShapeTabProps> = ({
  land,
  metrics,
  altimetryData,
  terrainAltimetryData,
  polygonChartRef,
  translations,
  onDownloadPolygon,
}) => {
  // LiDAR ground truth is authoritative for slope/aspect when present; otherwise
  // fall back to the API altimetry grid. Null ⇒ no card (parcel has no terrain).
  const terrainData = terrainAltimetryData ?? altimetryData
  const terrainMetrics = terrainData
    ? calculateTerrainMetrics(terrainData)
    : null

  const translateAspect = (aspect?: string): string => {
    if (!aspect) return translations?.status?.na || 'N/A'
    const dirMap: Record<string, string | undefined> = {
      North: translations?.directions?.north,
      'North-East': translations?.directions?.northEast,
      East: translations?.directions?.east,
      'South-East': translations?.directions?.southEast,
      South: translations?.directions?.south,
      'South-West': translations?.directions?.southWest,
      West: translations?.directions?.west,
      'North-West': translations?.directions?.northWest,
      Flat: translations?.directions?.flat,
    }
    return dirMap[aspect] || aspect
  }

  // Sun-exposure quality derived from the facing (downslope) direction.
  const getSunExposure = (aspect?: string) => {
    if (!aspect || aspect === 'Flat')
      return {
        arrow: '#9ca3af',
        text: '#4b5563',
        bg: 'rgba(107, 114, 128, 0.12)',
        label: translations?.status?.neutral || 'Neutral',
        note: translations?.sunExposureNotes?.neutral || 'No dominant slope',
      }
    if (aspect.includes('South'))
      return {
        arrow: '#00b341',
        text: '#067d33',
        bg: 'rgba(0, 179, 65, 0.12)',
        label: translations?.status?.excellent || 'Excellent',
        note:
          translations?.sunExposureNotes?.excellent ||
          'Passive-heating potential',
      }
    if (aspect.includes('East') || aspect.includes('West'))
      return {
        arrow: '#ff9500',
        text: '#b45309',
        bg: 'rgba(255, 149, 0, 0.14)',
        label: translations?.status?.moderate || 'Moderate',
        note: translations?.sunExposureNotes?.moderate || 'Balanced daylight',
      }
    return {
      arrow: '#ff3b30',
      text: '#b42318',
      bg: 'rgba(255, 59, 48, 0.12)',
      label: translations?.status?.poor || 'Poor',
      note: translations?.sunExposureNotes?.poor || 'Limited direct sun',
    }
  }

  const aspect = terrainMetrics?.primaryAspect
  const azimuth = aspect ? ASPECT_AZIMUTH[aspect] : undefined
  const sun = getSunExposure(aspect)
  const arrowRad = azimuth !== undefined ? (azimuth * Math.PI) / 180 : 0
  const arrowX = 64 + 34 * Math.sin(arrowRad)
  const arrowY = 64 - 34 * Math.cos(arrowRad)

  return (
    <Grid $columns={2}>
      <Card $span={2}>
        <CardHeader>
          <CardTitle>
            {translations?.sections?.plotBoundaryVisualization ||
              'Plot Boundary Visualization'}
          </CardTitle>
          {land?.geo_polygon && onDownloadPolygon && (
            <IconButton
              size="small"
              variant="secondary"
              onClick={onDownloadPolygon}
              aria-label="Download chart as PNG"
              title="Download PNG"
            >
              <Icon name="download" size="small" />
            </IconButton>
          )}
        </CardHeader>
        {land?.geo_polygon ? (
          <ChartContainer ref={polygonChartRef} style={{ height: '300px' }} />
        ) : (
          <div
            style={{
              height: '300px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#999',
              fontSize: '14px',
            }}
          >
            {translations?.noPolygonData || 'No polygon data available'}
          </div>
        )}
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            {translations?.sections?.shapeMetrics || 'Shape Metrics'}
          </CardTitle>
        </CardHeader>
        <MetricGrid>
          <MetricRow>
            <MetricLabel>
              {translations?.metrics?.shapeFactor || 'Shape Factor'}
              <InfoTooltip>
                <TooltipMetricExplanation
                  title={
                    translations?.tooltips?.shapeFactor?.title || 'Shape Factor'
                  }
                  shortDescription={
                    translations?.tooltips?.shapeFactor?.shortDescription ||
                    'Geometric circularity measure'
                  }
                  calculation={
                    translations?.tooltips?.shapeFactor?.calculation ||
                    '4π × Area / Perimeter²'
                  }
                  categories={
                    translations?.tooltips?.shapeFactor?.categories || [
                      {
                        label: 'Perfect circle',
                        range: '1.00',
                        description: 'Most efficient shape',
                      },
                      {
                        label: 'Square',
                        range: '0.785',
                        description: 'Excellent efficiency',
                      },
                      {
                        label: 'Regular hexagon',
                        range: '0.698',
                        description: 'Very good efficiency',
                      },
                      {
                        label: 'Equilateral triangle',
                        range: '0.604',
                        description: 'Good efficiency',
                      },
                      {
                        label: 'Irregular/elongated',
                        range: '<0.5',
                        description: 'Poor efficiency',
                      },
                    ]
                  }
                  importance={
                    translations?.tooltips?.shapeFactor?.importance ||
                    'Higher values indicate more efficient perimeter-to-area ratios, reducing fencing costs and maximizing usable central space'
                  }
                  limitations={
                    translations?.tooltips?.shapeFactor?.limitations ||
                    'Very low values (<0.3) suggest extremely irregular or narrow plots that may have development restrictions'
                  }
                  source={
                    translations?.tooltips?.shapeFactor?.source ||
                    'Federal Office of Topography swisstopo'
                  }
                />
              </InfoTooltip>
            </MetricLabel>
            <MetricValue
              style={{
                color:
                  metrics.shapeFactor >= 0.785
                    ? '#00B341' // Excellent (square or better)
                    : metrics.shapeFactor >= 0.698
                      ? '#52C41A' // Very good (hexagon or better)
                      : metrics.shapeFactor >= 0.604
                        ? '#FFA500' // Good (triangle or better)
                        : metrics.shapeFactor >= 0.5
                          ? '#FF9500' // Fair
                          : '#FF3B30', // Poor (irregular/elongated)
              }}
            >
              {metrics.shapeFactor.toFixed(3)}
            </MetricValue>
          </MetricRow>
          <MetricRow>
            <MetricLabel>
              {translations?.metrics?.regularity || 'Regularity'}
              <InfoTooltip>
                <TooltipMetricExplanation
                  title={
                    translations?.tooltips?.regularity?.title ||
                    'Regularity Classification'
                  }
                  shortDescription={
                    translations?.tooltips?.regularity?.shortDescription ||
                    'Shape efficiency assessment'
                  }
                  calculation={
                    translations?.tooltips?.regularity?.calculation ||
                    'Based on rectangularity score - how well the shape fits within its minimum bounding rectangle'
                  }
                  categories={
                    translations?.tooltips?.regularity?.categories || [
                      {
                        label: 'Regular',
                        range: '>85%',
                        description:
                          'Near-rectangular, optimal for development',
                      },
                      {
                        label: 'Moderate',
                        range: '70-85%',
                        description: 'Some irregularity, good potential',
                      },
                      {
                        label: 'Irregular',
                        range: '<70%',
                        description: 'Complex shape, planning challenges',
                      },
                    ]
                  }
                  importance={
                    translations?.tooltips?.regularity?.importance ||
                    'Regular shapes allow standard building designs, efficient utility routing, and maximum land utilization'
                  }
                  limitations={
                    translations?.tooltips?.regularity?.limitations ||
                    'Irregular shapes may require custom architectural solutions and have higher infrastructure costs'
                  }
                  source={
                    translations?.tooltips?.regularity?.source ||
                    'Federal Office of Topography swisstopo'
                  }
                />
              </InfoTooltip>
            </MetricLabel>
            <Badge
              variant={
                metrics.regularity === 'Regular'
                  ? 'success'
                  : metrics.regularity === 'Moderate'
                    ? 'warning'
                    : 'danger'
              }
            >
              {metrics.regularity === 'Regular'
                ? translations?.status?.regular || 'Regular'
                : metrics.regularity === 'Moderate'
                  ? translations?.status?.moderate || 'Moderate'
                  : translations?.status?.irregular || 'Irregular'}
            </Badge>
          </MetricRow>
          <MetricRow>
            <MetricLabel>
              {translations?.metrics?.compactness || 'Compactness'}
              <InfoTooltip>
                <TooltipMetricExplanation
                  title={
                    translations?.tooltips?.compactness?.title ||
                    'Compactness Ratio'
                  }
                  shortDescription={
                    translations?.tooltips?.compactness?.shortDescription ||
                    'Perimeter efficiency metric'
                  }
                  calculation={
                    translations?.tooltips?.compactness?.calculation ||
                    'Area / (Perimeter² / 4π) - normalized so perfect circle = 100%'
                  }
                  categories={
                    translations?.tooltips?.compactness?.categories || [
                      {
                        label: 'Excellent',
                        range: '>70%',
                        description: 'Very efficient shape',
                      },
                      {
                        label: 'Good',
                        range: '50-70%',
                        description: 'Reasonable efficiency',
                      },
                      {
                        label: 'Fair',
                        range: '30-50%',
                        description: 'Some inefficiency',
                      },
                      {
                        label: 'Poor',
                        range: '<30%',
                        description: 'Very inefficient',
                      },
                    ]
                  }
                  importance={
                    translations?.tooltips?.compactness?.importance ||
                    'Lower compactness means longer perimeter relative to area, increasing costs for fencing, utilities, and maintenance'
                  }
                  limitations={
                    translations?.tooltips?.compactness?.limitations ||
                    'Elongated or star-shaped parcels have low compactness and may require creative development strategies'
                  }
                  source={
                    translations?.tooltips?.compactness?.source ||
                    'Federal Office of Topography swisstopo'
                  }
                />
              </InfoTooltip>
            </MetricLabel>
            <ProgressBar>
              <ProgressFill
                $percentage={metrics.compactness * 100}
                $color={
                  metrics.compactness > 0.7
                    ? '#00B341'
                    : metrics.compactness > 0.5
                      ? '#FF9500'
                      : '#FF3B30'
                }
              />
            </ProgressBar>
            <MetricValue>{(metrics.compactness * 100).toFixed(0)}%</MetricValue>
          </MetricRow>
          <MetricRow>
            <MetricLabel>
              {translations?.metrics?.dimensions || 'Dimensions (L × W)'}
              <InfoTooltip>
                <TooltipMetricExplanation
                  title={
                    translations?.tooltips?.dimensions?.title ||
                    'Parcel Dimensions'
                  }
                  shortDescription={
                    translations?.tooltips?.dimensions?.shortDescription ||
                    'Effective length and width'
                  }
                  calculation={
                    translations?.tooltips?.dimensions?.calculation ||
                    'Uses Minimum Bounding Rectangle (MBR) algorithm to find the smallest rectangle that contains the entire parcel, then reports its dimensions'
                  }
                  categories={
                    translations?.tooltips?.dimensions?.categories || [
                      {
                        label: 'Building orientation',
                        description: 'Planning building placement',
                      },
                      {
                        label: 'Access roads',
                        description: 'Road placement optimization',
                      },
                      {
                        label: 'Setback calculations',
                        description: 'Legal distance requirements',
                      },
                      {
                        label: 'Solar panels',
                        description: 'Array sizing calculations',
                      },
                      {
                        label: 'Landscape design',
                        description: 'Garden and outdoor planning',
                      },
                    ]
                  }
                  importance={
                    translations?.tooltips?.dimensions?.importance ||
                    'These are effective dimensions for planning purposes, not necessarily the actual distances between specific boundary points'
                  }
                  limitations={
                    translations?.tooltips?.dimensions?.limitations ||
                    'Irregular shapes may not fully utilize the bounding rectangle area. Check area efficiency metric'
                  }
                  source={
                    translations?.tooltips?.dimensions?.source ||
                    'Federal Office of Topography swisstopo'
                  }
                />
              </InfoTooltip>
            </MetricLabel>
            <MetricValue>
              {Math.round(metrics.length)} × {Math.round(metrics.width)} m
            </MetricValue>
          </MetricRow>
          <MetricRow>
            <MetricLabel>
              {translations?.metrics?.areaEfficiency || 'Area Efficiency'}
              <InfoTooltip>
                <TooltipMetricExplanation
                  title={
                    translations?.tooltips?.areaEfficiency?.title ||
                    'Area Efficiency'
                  }
                  shortDescription={
                    translations?.tooltips?.areaEfficiency?.shortDescription ||
                    'Land utilization within bounding box'
                  }
                  calculation={
                    translations?.tooltips?.areaEfficiency?.calculation ||
                    '(Actual parcel area / Bounding rectangle area) × 100%'
                  }
                  categories={
                    translations?.tooltips?.areaEfficiency?.categories || [
                      {
                        label: 'Excellent',
                        range: '90-100%',
                        description: 'Nearly rectangular shape',
                      },
                      {
                        label: 'Good',
                        range: '75-90%',
                        description: 'Minor irregularities',
                      },
                      {
                        label: 'Moderate',
                        range: '60-75%',
                        description: 'Notable corners missing',
                      },
                      {
                        label: 'Poor',
                        range: '<60%',
                        description: 'Very irregular shape',
                      },
                    ]
                  }
                  importance={
                    translations?.tooltips?.areaEfficiency?.importance ||
                    "Lower efficiency indicates 'dead spaces' or irregular corners that may be difficult to develop or access"
                  }
                  limitations={
                    translations?.tooltips?.areaEfficiency?.limitations ||
                    'Low efficiency (<50%) suggests the parcel has significant irregular features that will require special planning consideration'
                  }
                  source={
                    translations?.tooltips?.areaEfficiency?.source ||
                    'Federal Office of Topography swisstopo'
                  }
                />
              </InfoTooltip>
            </MetricLabel>
            <MetricValue>
              {(
                (metrics.area / (metrics.length * metrics.width)) *
                100
              ).toFixed(0)}
              %
            </MetricValue>
          </MetricRow>
        </MetricGrid>
      </Card>

      {/* Slope & sun — the direction the ground faces (downslope) + the sun
          exposure that follows. Terrain data, so only shown when altimetry
          exists (and isn't the <4-point 'Unknown' degenerate). */}
      {terrainMetrics && terrainMetrics.primaryAspect !== 'Unknown' && (
        <Card>
          <CardHeader>
            <CardTitle>
              {translations?.sections?.slopeSunOrientation || 'Slope & Sun'}
              <InfoTooltip>
                <TooltipMetricExplanation
                  title={
                    translations?.tooltips?.dominantOrientation?.title ||
                    'Dominant Orientation'
                  }
                  shortDescription={
                    translations?.tooltips?.dominantOrientation
                      ?.shortDescription ||
                    'Primary compass direction the terrain faces'
                  }
                  calculation={
                    translations?.tooltips?.dominantOrientation?.calculation ||
                    'Slope-weighted average of the downslope direction across the parcel, from the SwissAlti3D elevation grid'
                  }
                  importance={
                    translations?.tooltips?.dominantOrientation?.importance ||
                    'Affects solar gain, heating/cooling, and outdoor living. Southerly faces get the most sun'
                  }
                  limitations={
                    translations?.tooltips?.dominantOrientation?.limitations ||
                    'Local variations may differ. Consider micro-climates and surrounding structures'
                  }
                  source={
                    translations?.tooltips?.dominantOrientation?.source ||
                    'SwissAlti3D - Federal Office of Topography swisstopo'
                  }
                />
              </InfoTooltip>
            </CardTitle>
          </CardHeader>
          <div
            style={{
              display: 'flex',
              gap: '18px',
              alignItems: 'center',
              padding: '16px',
            }}
          >
            <svg
              width="132"
              height="132"
              viewBox="0 0 128 128"
              style={{ flex: 'none' }}
              role="img"
              aria-label={`Slope faces ${aspect || 'flat'}`}
            >
              {/* sunny (south) half */}
              <path
                d="M64,64 L109,90 A52,52 0 0 1 18.98,90 Z"
                fill="rgba(0, 179, 65, 0.12)"
              />
              <circle cx="64" cy="64" r="52" fill="none" stroke="#e5e7eb" />
              <line x1="64" y1="12" x2="64" y2="20" stroke="#c7ccd1" />
              <line x1="64" y1="108" x2="64" y2="116" stroke="#c7ccd1" />
              <line x1="12" y1="64" x2="20" y2="64" stroke="#c7ccd1" />
              <line x1="108" y1="64" x2="116" y2="64" stroke="#c7ccd1" />
              <text
                x="64"
                y="10"
                textAnchor="middle"
                fontSize="9"
                fill="#9ca3af"
              >
                N
              </text>
              <text
                x="64"
                y="126"
                textAnchor="middle"
                fontSize="9"
                fill="#067d33"
              >
                S
              </text>
              <text
                x="122"
                y="67"
                textAnchor="middle"
                fontSize="9"
                fill="#9ca3af"
              >
                E
              </text>
              <text
                x="6"
                y="67"
                textAnchor="middle"
                fontSize="9"
                fill="#9ca3af"
              >
                W
              </text>
              {azimuth !== undefined && (
                <>
                  <defs>
                    <marker
                      id="slope-arrow-head"
                      markerWidth="7"
                      markerHeight="7"
                      refX="5"
                      refY="3.5"
                      orient="auto"
                    >
                      <path d="M0,0 L7,3.5 L0,7 Z" fill={sun.arrow} />
                    </marker>
                  </defs>
                  <line
                    x1="64"
                    y1="64"
                    x2={arrowX}
                    y2={arrowY}
                    stroke={sun.arrow}
                    strokeWidth="4"
                    markerEnd="url(#slope-arrow-head)"
                  />
                </>
              )}
              <circle cx="64" cy="64" r="3.5" fill="#374151" />
            </svg>
            <MetricGrid style={{ flex: 1 }}>
              <MetricRow>
                <MetricLabel>
                  {translations?.metrics?.faces || 'Faces'}
                </MetricLabel>
                <MetricValue style={{ color: sun.text }}>
                  {translateAspect(aspect)}
                </MetricValue>
              </MetricRow>
              <MetricRow>
                <MetricLabel>
                  {translations?.metrics?.averageSlope || 'Average Slope'}
                </MetricLabel>
                <MetricValue>
                  {terrainMetrics.averageSlope.toFixed(1)}°
                </MetricValue>
              </MetricRow>
              <MetricRow style={{ borderBottom: 'none' }}>
                <MetricLabel>
                  {translations?.metrics?.sunExposure || 'Sun exposure'}
                </MetricLabel>
                <MetricValue>
                  <span
                    style={{
                      display: 'inline-block',
                      padding: '2px 10px',
                      borderRadius: '999px',
                      fontSize: '12px',
                      fontWeight: 700,
                      background: sun.bg,
                      color: sun.text,
                    }}
                  >
                    {sun.label}
                  </span>
                </MetricValue>
              </MetricRow>
              <div
                style={{
                  fontSize: '11px',
                  color: '#9ca3af',
                  marginTop: '4px',
                }}
              >
                {sun.note}
              </div>
            </MetricGrid>
          </div>
        </Card>
      )}
    </Grid>
  )
}
